// eslint-disable-next-line no-unused-vars
import 'regenerator-runtime/runtime';
import React, { useState, useCallback, useRef } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';

// Components
import { DataTableFilter } from '../DataTableFilter';
import { DataFilterByColumnMenu } from '../DataFilterByColumnMenu';

// Hooks
import { useFocusOutside } from '../../../../common/hooks/useFocusOutside';

const DataTableMasthead = styled(({ display, ...rest }) => <div {...rest} />)`
    display: ${(props) => (props.display ? 'flex' : 'none')};
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    visibility: ${(props) => (props.display ? 'visible' : 'hidden')};
`;

const TableLabel = styled.h3`
    color: ${(props) => props.theme.colors.text.default};
    font-family: ${(props) => props.theme.typography.fonts.ui};
    font-size: 1.337rem;
    font-weight: 600;
    line-height: 2rem;
    margin: 1.337rem 0;
`;

const HeaderButtons = styled.div`
    display: flex;
    align-items: center;
`;

export const DataTableHeader = ({
    displayLabel,
    filterPlaceholder,
    globalFilterContext,
    setGlobalFilter,
    filterColumn,
    setFilterColumn,
    columns,
    id,
    onFilterChange,
    filterWidth,
    maxFilterWidth,
    headerButtons,
    label,
    ...props
}) => {
    const filterID = `${id}__filter`;
    const [showFilterColumnOptions, setShowFilterColumnOptions] = useState(false);
    const containerRef = useRef(null);
    const filterInputRef = useRef(null);
    const filterableColumns = React.useMemo(() => columns.filter((column) => column.filterable), [columns]);
    const [filteredColumns, setFilteredColumns] = React.useState(filterableColumns);
    const updateFilteredColumns = (val) => {
        if (val && val !== '#') {
            const data = val.substring(1).toLowerCase();

            return setFilteredColumns(filterableColumns.filter((col) => col.Header.toLowerCase().includes(data)));
        } else {
            setFilteredColumns(filterableColumns);
        }
    };
    const onChangeWithColumnSelect = useCallback((value) => {
        if (value?.[0] === '#') {
            setGlobalFilter('');
            setShowFilterColumnOptions(true);
            updateFilteredColumns(value);
        } else {
            setShowFilterColumnOptions(false);
            onFilterChange(value || undefined);
            updateFilteredColumns();
        }
    }, [setGlobalFilter, setShowFilterColumnOptions, onFilterChange]);
    const clickOutsideCallback = () => {
        setShowFilterColumnOptions(false);
    };
    useFocusOutside(containerRef, clickOutsideCallback);

    return (
        <DataTableMasthead display={displayLabel}>
            <TableLabel id={id}>{label}</TableLabel>
            {headerButtons.length > 0 && <HeaderButtons>{headerButtons}</HeaderButtons>}
            {typeof onFilterChange === 'function' && (
                <div ref={containerRef} style={{ flexBasis: filterWidth, maxWidth: maxFilterWidth ?? 'none', position: 'relative' }}>
                    <DataTableFilter
                        id={filterID}
                        list={filterID}
                        ref={filterInputRef}
                        onChange={(value) => {
                            if (filterableColumns.length > 0) {
                                onChangeWithColumnSelect(value);
                            } else {
                                onFilterChange(value || undefined);
                            }
                        }}
                        placeholder={filterPlaceholder}
                        filterColumn={filterColumn}
                        setFilterColumn={setFilterColumn}
                        autocomplete="false"
                        name="noAutocomplete"
                        showFilterColumnOptions={showFilterColumnOptions}
                        setShowFilterColumnOptions={setShowFilterColumnOptions}
                    />
                    {showFilterColumnOptions && (
                        <DataFilterByColumnMenu
                            id={filterID}
                            onSelect={(column) => {
                                setFilterColumn({
                                    label: column.Header,
                                    accessor: column.accessor
                                });
                                setShowFilterColumnOptions(false);
                                setGlobalFilter('');
                                filterInputRef?.current?.clear?.();
                                filterInputRef?.current?.inputFocus?.();
                            }}
                            containerRef={containerRef}
                            filterableColumns={filteredColumns}
                            setShowFilterColumnOptions={setShowFilterColumnOptions}
                        />
                    )}
                </div>
            )}
        </DataTableMasthead>
    );
};

DataTableHeader.propTypes = {
    displayLabel: PropTypes.bool,
    filterWidth: PropTypes.string,
    maxFilterWidth: PropTypes.string,
    filterPlaceholder: PropTypes.string,
    headerButtons: PropTypes.array,
    id: PropTypes.string,
    setGlobalFilter: PropTypes.func,
    onFilterChange: PropTypes.func,
    label: PropTypes.string,
    globalFilterContext: PropTypes.string,
    showFilterColumnOptions: PropTypes.bool,
    setShowFilterColumnOptions: PropTypes.func,
    filterColumn: PropTypes.object,
    setFilterColumn: PropTypes.func,
    columns: PropTypes.array
};

DataTableHeader.defaultProps = {
    globalFilterContext: ``,
    showFilterColumnOptions: false,
    filterWidth: '33.3%',
    headerButtons: [],
    setGlobalFilter: () => {},
    setShowFilterColumnOptions: () => {},
    columns: []
};
